/** COTELE ELEMENTULUI ALES pe machetă (Cătălin, 13.10.2026: «niște cote pe
 *  elementul selectat, de lungime, lățime»).
 *
 *  Se MĂSOARĂ PE CE E DESENAT, în reperul feței elementului: `u` în lungul ei,
 *  `w` spre afară, `z` pe verticală. Un element fără față (o placă de balcon pe
 *  colț, un gol fără față scrisă) își ia direcția principală din propriile
 *  puncte. Se cotează dimensiunile de cel puțin 0,30 m (lungime, înălțime,
 *  adâncime), cel puțin două: fereastra primește lățime și înălțime, placa
 *  balconului lățime și adâncime, o jardinieră și grosimea ei.
 *
 *  Desenul: linie de cotă cu linii ajutătoare și tăieturi la capete, ca pe o
 *  planșă, scoasă în fața elementului, spre cameră; cifra pe o etichetă albă
 *  de mărime fixă pe ecran. Totul peste scenă (`depthTest: false`). */
import * as THREE from "three";
import type { LiniaFetei } from "./selectie3d";

type V3 = [number, number, number];

export interface Cadru {
  d: [number, number];
  n: [number, number];
  u0: number; u1: number;
  w0: number; w1: number;
  z0: number; z1: number;
}

/** Cadrul elementului din punctele lui (x, y, z la rând). `fata` dă direcția;
 *  fără ea, direcția principală a punctelor în plan. `spre` = camera: normala
 *  se întoarce spre ea, ca cotele să iasă pe partea văzută. */
export function cadrulElementului(puncte: ArrayLike<number>, fata: LiniaFetei | null, spre?: V3): Cadru | null {
  const n = Math.floor(puncte.length / 3);
  if (!n) return null;
  let d: [number, number];
  if (fata) d = [fata.d[0], fata.d[1]];
  else {
    let mx = 0, my = 0;
    for (let i = 0; i < n; i++) { mx += puncte[i * 3]; my += puncte[i * 3 + 1]; }
    mx /= n; my /= n;
    let sxx = 0, syy = 0, sxy = 0;
    for (let i = 0; i < n; i++) {
      const x = puncte[i * 3] - mx, y = puncte[i * 3 + 1] - my;
      sxx += x * x; syy += y * y; sxy += x * y;
    }
    const a = 0.5 * Math.atan2(2 * sxy, sxx - syy);
    d = [Math.cos(a), Math.sin(a)];
  }
  let nn: [number, number] = fata ? [fata.n[0], fata.n[1]] : [-d[1], d[0]];
  const c = { u0: Infinity, u1: -Infinity, w0: Infinity, w1: -Infinity, z0: Infinity, z1: -Infinity };
  const masoara = () => {
    c.u0 = c.w0 = c.z0 = Infinity; c.u1 = c.w1 = c.z1 = -Infinity;
    for (let i = 0; i < n; i++) {
      const x = puncte[i * 3], y = puncte[i * 3 + 1], z = puncte[i * 3 + 2];
      const u = x * d[0] + y * d[1], w = x * nn[0] + y * nn[1];
      if (u < c.u0) c.u0 = u; if (u > c.u1) c.u1 = u;
      if (w < c.w0) c.w0 = w; if (w > c.w1) c.w1 = w;
      if (z < c.z0) c.z0 = z; if (z > c.z1) c.z1 = z;
    }
  };
  masoara();
  if (spre) {
    const wm = (c.w0 + c.w1) / 2;
    if (spre[0] * nn[0] + spre[1] * nn[1] < wm) { nn = [-nn[0], -nn[1]]; masoara(); }
  }
  return { d, n: nn, ...c };
}

export interface Cota {
  /** Capetele liniei de cotă, în lume. */
  a: V3;
  b: V3;
  /** Liniile ajutătoare (perechi de puncte), de la element la linia de cotă. */
  ajutatoare: V3[];
  /** Tăieturile de la capete (perechi de puncte). */
  taieturi: V3[];
  valoare: number;
  fel: "lungime" | "inaltime" | "adancime";
}

/** Cotele cadrului: dimensiunile de cel puțin `min` metri, cel puțin două.
 *  `spre` = camera, ca înălțimea și adâncimea să stea pe latura văzută. */
export function cotele(c: Cadru, spre?: V3, min = 0.3): Cota[] {
  const L = c.u1 - c.u0, D = c.w1 - c.w0, H = c.z1 - c.z0;
  const dims = ([["lungime", L], ["inaltime", H], ["adancime", D]] as [Cota["fel"], number][])
    .filter(([, v]) => v > 0.02);
  let alese = dims.filter(([, v]) => v >= min);
  if (alese.length < 2) alese = [...dims].sort((a, b) => b[1] - a[1]).slice(0, 2);
  const fel = new Set(alese.map(([f]) => f));
  const off = Math.min(1.2, Math.max(0.3, 0.06 * Math.max(L, H, D)));
  const tick = Math.min(0.18, Math.max(0.08, off * 0.35));
  const P = (u: number, w: number, z: number): V3 =>
    [c.d[0] * u + c.n[0] * w, c.d[1] * u + c.n[1] * w, z];
  // latura văzută: capătul feței spre cameră
  const uc = spre ? spre[0] * c.d[0] + spre[1] * c.d[1] : c.u1;
  const sus = !fel.has("inaltime");          // un element culcat: cotele pe fața lui de sus
  const us = uc >= (c.u0 + c.u1) / 2 ? c.u1 + off : c.u0 - off;
  const uLat = uc >= (c.u0 + c.u1) / 2 ? c.u1 : c.u0;
  const out: Cota[] = [];
  const taieturi = (a: V3, b: V3, perp: V3): V3[] => {
    const dl = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const ll = Math.hypot(dl[0], dl[1], dl[2]) || 1;
    const k: V3 = [(dl[0] / ll + perp[0]) * tick / Math.SQRT2, (dl[1] / ll + perp[1]) * tick / Math.SQRT2,
                   (dl[2] / ll + perp[2]) * tick / Math.SQRT2];
    return [a, b].flatMap((p) => [[p[0] - k[0], p[1] - k[1], p[2] - k[2]] as V3,
                                   [p[0] + k[0], p[1] + k[1], p[2] + k[2]] as V3]);
  };
  if (fel.has("lungime")) {
    if (sus) {
      const w = c.w1 + off, z = c.z1;
      const a = P(c.u0, w, z), b = P(c.u1, w, z);
      out.push({ a, b, valoare: L, fel: "lungime",
                 ajutatoare: [P(c.u0, c.w1, z), P(c.u0, w + 0.1, z), P(c.u1, c.w1, z), P(c.u1, w + 0.1, z)],
                 taieturi: taieturi(a, b, [c.n[0], c.n[1], 0]) });
    } else {
      const z = c.z0 - off, w = c.w1;
      const a = P(c.u0, w, z), b = P(c.u1, w, z);
      out.push({ a, b, valoare: L, fel: "lungime",
                 ajutatoare: [P(c.u0, w, c.z0), P(c.u0, w, z - 0.1), P(c.u1, w, c.z0), P(c.u1, w, z - 0.1)],
                 taieturi: taieturi(a, b, [0, 0, 1]) });
    }
  }
  if (fel.has("inaltime")) {
    const w = c.w1;
    const a = P(us, w, c.z0), b = P(us, w, c.z1);
    const sens = us > uLat ? 1 : -1;
    out.push({ a, b, valoare: H, fel: "inaltime",
               ajutatoare: [P(uLat, w, c.z0), P(us + sens * 0.1, w, c.z0), P(uLat, w, c.z1), P(us + sens * 0.1, w, c.z1)],
               taieturi: taieturi(a, b, [c.d[0] * sens, c.d[1] * sens, 0]) });
  }
  if (fel.has("adancime")) {
    const z = sus ? c.z1 : c.z0;
    const a = P(us, c.w0, z), b = P(us, c.w1, z);
    const sens = us > uLat ? 1 : -1;
    out.push({ a, b, valoare: D, fel: "adancime",
               ajutatoare: [P(uLat, c.w0, z), P(us + sens * 0.1, c.w0, z), P(uLat, c.w1, z), P(us + sens * 0.1, c.w1, z)],
               taieturi: taieturi(a, b, [c.d[0] * sens, c.d[1] * sens, 0]) });
  }
  return out;
}

/** Eticheta unei cote: text închis pe alb, de mărime fixă pe ecran. */
function eticheta(text: string, deArs: { dispose(): void }[]): THREE.Sprite {
  const px = 30, padX = 12, padY = 7;
  const cv = document.createElement("canvas");
  const ctx = cv.getContext("2d")!;
  ctx.font = `600 ${px}px system-ui, -apple-system, Segoe UI, sans-serif`;
  const w = Math.ceil(ctx.measureText(text).width) + padX * 2, h = px + padY * 2;
  cv.width = w; cv.height = h;
  ctx.font = `600 ${px}px system-ui, -apple-system, Segoe UI, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.94)";
  ctx.strokeStyle = "rgba(14,59,70,0.55)";
  ctx.lineWidth = 2;
  const r = 9;
  ctx.beginPath();
  ctx.moveTo(r, 1); ctx.lineTo(w - r, 1); ctx.quadraticCurveTo(w - 1, 1, w - 1, r);
  ctx.lineTo(w - 1, h - r); ctx.quadraticCurveTo(w - 1, h - 1, w - r, h - 1);
  ctx.lineTo(r, h - 1); ctx.quadraticCurveTo(1, h - 1, 1, h - r);
  ctx.lineTo(1, r); ctx.quadraticCurveTo(1, 1, r, 1);
  ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#0e3b46";
  ctx.textBaseline = "middle";
  ctx.fillText(text, padX, h / 2 + 1);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, transparent: true,
                                         sizeAttenuation: false });
  deArs.push(tex, mat);
  const s = new THREE.Sprite(mat);
  const inalt = 0.024;                      // ~ 3 % din înălțimea ecranului
  s.scale.set(inalt * (w / h), inalt, 1);
  s.renderOrder = 31;
  return s;
}

/** Grupul cotelor, de pus în scenă; `scoate` îl ia înapoi și eliberează tot. */
export function deseneazaCotele(s3: THREE.Object3D, cote: readonly Cota[], text: (c: Cota) => string) {
  const grup = new THREE.Group();
  const deArs: { dispose(): void }[] = [];
  const linie = new THREE.LineBasicMaterial({ color: 0x082a33, depthTest: false });
  const ajut = new THREE.LineBasicMaterial({ color: 0x0e3b46, depthTest: false, transparent: true, opacity: 0.7 });
  deArs.push(linie, ajut);
  const v = (p: V3) => new THREE.Vector3(p[0], p[1], p[2]);
  for (const c of cote) {
    const gl = new THREE.BufferGeometry().setFromPoints([v(c.a), v(c.b), ...c.taieturi.map(v)]);
    const ga = new THREE.BufferGeometry().setFromPoints(c.ajutatoare.map(v));
    deArs.push(gl, ga);
    const ll = new THREE.LineSegments(gl, linie);
    const la = new THREE.LineSegments(ga, ajut);
    ll.renderOrder = la.renderOrder = 30;
    const s = eticheta(text(c), deArs);
    s.position.set((c.a[0] + c.b[0]) / 2, (c.a[1] + c.b[1]) / 2, (c.a[2] + c.b[2]) / 2);
    grup.add(la, ll, s);
  }
  s3.add(grup);
  return { grup, scoate: () => { s3.remove(grup); deArs.forEach((d) => d.dispose()); } };
}
